import { useEffect, useState, type FormEvent } from 'react';
import { KeyRound, Loader2, LogIn, ShieldCheck, Server } from 'lucide-react';
import { checkPassword } from '@bakery/shared';

import { api, ApiError, apiBase, setApiBase, type PublicBusiness } from '../lib/api.js';
import { openingSummary } from '../lib/opening-hours.js';
import { useAuth } from '../lib/auth.js';
import { Button, Field, Input, cx } from '../components/ui.js';

/** The wheat mark from the app shell, at a size that carries a whole screen. */
function Mark() {
  return (
    <span className="grid size-14 place-items-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-ink)]" aria-hidden>
      <svg viewBox="0 0 24 24" className="size-7" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3v18" strokeLinecap="round" />
        <path d="M12 6.5c-1.6-1.4-3.4-1.6-4.6-1.4-.2 1.2 0 3 1.4 4.6 1.4 1.4 3.2 1.4 3.2 1.4M12 6.5c1.6-1.4 3.4-1.6 4.6-1.4.2 1.2 0 3-1.4 4.6-1.4 1.4-3.2 1.4-3.2 1.4" strokeLinejoin="round" />
        <path d="M12 12.5c-1.6-1.4-3.4-1.6-4.6-1.4-.2 1.2 0 3 1.4 4.6 1.4 1.4 3.2 1.4 3.2 1.4M12 12.5c1.6-1.4 3.4-1.6 4.6-1.4.2 1.2 0 3-1.4 4.6-1.4 1.4-3.2 1.4-3.2 1.4" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

function Shell({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center bg-[var(--color-canvas)] px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <Mark />
          <h1 className="mt-3 font-display text-xl font-semibold">{title}</h1>
          <p className="mt-1 text-sm text-[var(--color-ink-soft)]">{subtitle}</p>
        </div>
        <div className="card p-5">{children}</div>
        <Shopfront />
      </div>
    </div>
  );
}

/**
 * Who this address belongs to, for anybody who is not staff.
 *
 * The app sits at the bakery's own domain and used to answer a visitor with a
 * password box and nothing else — no name, no address, no sign that a real
 * bakery is behind it. A customer following a link learned nothing, and anyone
 * checking the business was real found no evidence of it.
 *
 * It sits below the sign-in card rather than above, because the people who open
 * this page a hundred times a week are the staff, and they should not have to
 * read past the shopfront to reach the password field.
 *
 * Read from the server so it cannot drift from the receipts. If the request
 * fails — offline at the counter, which happens — nothing renders and signing
 * in is unaffected.
 */
function Shopfront() {
  const [shop, setShop] = useState<PublicBusiness | null>(null);

  useEffect(() => {
    let live = true;
    api
      .publicBusiness()
      .then((details) => live && setShop(details))
      .catch(() => {
        /* Offline, or an older server. The sign-in box is what matters. */
      });
    return () => {
      live = false;
    };
  }, []);

  if (!shop) return null;

  const { address } = shop;
  const street = [address.line1, address.line2].filter(Boolean).join(', ');
  const place = `${address.city}, ${address.province} ${address.postalCode}, ${address.country}`;

  return (
    <footer className="mt-8 text-center text-xs leading-relaxed text-[var(--color-ink-soft)]">
      <p className="font-display text-sm font-semibold text-[var(--color-ink)]">{shop.tradingName}</p>
      <p className="mt-0.5">Artisan breads, focaccia and pastries, baked fresh daily.</p>

      <address className="mt-3 not-italic">
        {street}
        <br />
        {place}
      </address>

      <p className="mt-3">
        <a className="underline" href={`tel:${shop.phone.replace(/\s/g, '')}`}>
          {shop.phone}
        </a>
        {' · '}
        <a className="underline" href={`mailto:${shop.email}`}>
          {shop.email}
        </a>
      </p>

      <div className="mt-3">
        {openingSummary(shop.openingHours).map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>

      {/*
        * One handle across both, which is how the shop writes it on its own
        * receipts. Each word links to its own place so the handle need only be
        * read once, rather than printed twice beside two near-identical links.
        */}
      {(shop.facebookUrl || shop.instagramHandle) && (
        <p className="mt-3">
          {shop.facebookUrl && (
            <a className="underline" href={shop.facebookUrl} target="_blank" rel="noreferrer">
              Facebook
            </a>
          )}
          {shop.facebookUrl && shop.instagramHandle && ' and '}
          {shop.instagramHandle && (
            <a
              className="underline"
              href={`https://www.instagram.com/${shop.instagramHandle.replace(/^@/, '')}`}
              target="_blank"
              rel="noreferrer"
            >
              Instagram
            </a>
          )}
          {shop.instagramHandle && <> {shop.instagramHandle}</>}
        </p>
      )}

      {/* The registered name, for anyone checking the business is real. */}
      <p className="mt-3 text-[var(--color-ink-faint)]">{shop.legalName}</p>
    </footer>
  );
}

/**
 * Would a relative "/api" find anything from here?
 *
 * The web app is served from the same origin as the API, so it would. The
 * Android build is served from inside itself, where "/api" resolves to a
 * localhost holding nothing — it must be told where the shop server is before
 * it can do anything at all.
 *
 * Detected by asking whether this page came over http(s) from a real host,
 * rather than by looking for `window.Capacitor`. That was the first attempt and
 * it failed on a real handset: nothing in this app imports `@capacitor/core`, so
 * the global is not dependable at render time. The address field never appeared,
 * and the app opened on a sign-in screen that could not succeed and offered no
 * way out.
 */
/**
 * Tolerate a bare host.
 *
 * Someone reading an address off a router types "192.168.1.20:4000", not the
 * scheme and the /api suffix, and a field that rejects that is a field that gets
 * typed wrong three times at a counter.
 */
function normalise(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `http://${trimmed}`;
  const withoutTrailingSlash = withScheme.replace(/\/+$/, '');
  return /\/api$/i.test(withoutTrailingSlash) ? withoutTrailingSlash : `${withoutTrailingSlash}/api`;
}

function relativeApiWouldWork(): boolean {
  if (typeof location === 'undefined') return false;
  const httpish = location.protocol === 'http:' || location.protocol === 'https:';
  const realHost = location.hostname !== '' && location.hostname !== 'localhost';
  // A dev server on localhost is the ordinary web case and does work.
  return httpish && (realHost || location.port !== '');
}

/**
 * Where the shop server is, settable before signing in.
 *
 * This exists because of a deadlock that made the Android build unusable: the
 * address lived only in Settings, Settings needs a signed-in user, and signing
 * in needs the address. The app opened on a sign-in screen that could never
 * succeed and offered no way out.
 *
 * Shown automatically on the Android build until an address is saved, and
 * available on demand everywhere else — a browser pointed at the wrong host has
 * the same problem, just less often.
 */
function ServerAddress({ onSaved }: { onSaved: () => void }) {
  const [value, setValue] = useState(() => (apiBase() === '/api' ? '' : apiBase()));
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  /**
   * Ask the address whether anything is there, and say precisely what came back.
   *
   * Setting up a device is the one moment when "it did not work" is useless and
   * "connection refused at http://192.168.1.20:4000/api" solves it in seconds.
   * The person doing this is standing in a shop with a phone, not reading a
   * console.
   */
  async function testConnection() {
    setTesting(true);
    setResult(null);

    const base = normalise(value) || apiBase();
    const startedAt = Date.now();
    try {
      const response = await fetch(`${base}/health`, { signal: AbortSignal.timeout(8000) });
      if (!response.ok) {
        setResult({ ok: false, text: `${base} answered ${response.status}. Something is there, but it is not the shop server.` });
        return;
      }
      const payload = (await response.json()) as { ok?: boolean };
      setResult(
        payload?.ok
          ? { ok: true, text: `Reached the shop server at ${base}.` }
          : { ok: false, text: `${base} answered, but not like the shop server.` },
      );
    } catch (error) {
      if ((error as Error)?.name === 'TimeoutError') {
        setResult({
          ok: false,
          text: `No answer from ${base} within 8 seconds. Check the address, that both devices are on the same wifi, and that the shop PC's firewall allows port 4000.`,
        });
        return;
      }

      /*
       * An instant failure is ambiguous, and the old message guessed wrong.
       *
       * It can mean nothing is at that address — or that the device reached the
       * server perfectly well and its own browser then refused the answer. Those
       * need opposite responses: one is fixed at the router, the other cannot be
       * fixed there at all. Telling a shop to check its wifi while the server
       * log shows the connection arriving wastes an afternoon; it wasted one
       * here.
       *
       * A no-cors request is the discriminator. It is exempt from the
       * cross-origin checks that reject an ordinary fetch, so if it completes,
       * the address was reachable the whole time.
       */
      // Timed before the probe below, not after: how fast the first attempt
      // failed is the diagnostic, and folding the retry into it hides that.
      const ms = Date.now() - startedAt;

      let reachedAnyway = false;
      try {
        await fetch(`${base}/health`, { mode: 'no-cors', signal: AbortSignal.timeout(5000) });
        reachedAnyway = true;
      } catch {
        /* genuinely could not get there */
      }

      setResult({
        ok: false,
        text: reachedAnyway
          ? `Reached ${base}, but this device refused to accept the answer. The server and the network are fine — something on this device is blocking it, so compare its Android and WebView version against a device that works.`
          : navigator.onLine === false
            ? 'This device reports no network connection at all. Connect it to the shop wifi and try again.'
            : `Could not reach ${base} — failed in ${ms}ms. Check the address and that both devices are on the same wifi.`,
      });
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface-sunken)] p-3">
      <Field
        label="Shop server address"
        hint="The machine running the shop system, for example http://192.168.1.20:4000/api"
      >
        <Input
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setSaved(false);
          }}
          placeholder="http://192.168.1.20:4000/api"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          inputMode="url"
        />
      </Field>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          onClick={() => {
            const withPath = normalise(value);
            if (!withPath) return;
            setApiBase(withPath);
            setValue(withPath);
            setSaved(true);
            setResult(null);
            onSaved();
          }}
        >
          Save address
        </Button>

        <Button size="sm" variant="secondary" loading={testing} onClick={testConnection}>
          Test connection
        </Button>

        {saved && !result && <span className="text-xs text-[var(--color-ok)]">Saved.</span>}
      </div>

      {result && (
        <p
          className={cx(
            'mt-2 rounded-[var(--radius-md)] px-2.5 py-2 text-xs',
            result.ok
              ? 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]'
              : 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
          )}
        >
          {result.text}
        </p>
      )}
    </div>
  );
}

export function SignInScreen() {
  const { signIn } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /*
   * Open on the address field when the Android build has never been told one.
   * Anything else is a screen the person cannot get past.
   */
  const [showServer, setShowServer] = useState(() => apiBase() === '/api' && !relativeApiWouldWork());
  const [attempted, setAttempted] = useState(false);

  // A failure to reach the server is the moment the address becomes relevant.
  useEffect(() => {
    if (attempted && error && /reach|not reachable|connection/i.test(error)) setShowServer(true);
  }, [attempted, error]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(identifier.trim(), password);
      setAttempted(true);
    } catch (caught) {
      setAttempted(true);
      /*
       * Show what actually went wrong.
       *
       * Anything but an ApiError used to become "could not reach the shop
       * server" — which, once sign-in could happen on the device itself, was
       * often a lie. Someone mistyping their password with the internet down was
       * told to wait for a connection instead of trying again, and a device that
       * had never seen the account said nothing about why.
       */
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : 'Could not sign in. Check the login ID and password.',
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell title="ANA's Bakery" subtitle="Sign in to the shop">
      <form onSubmit={submit} className="space-y-4">
        {/*
         * At the top, not beside the button.
         *
         * It used to sit between the password field and Sign in, which on a
         * phone — with the address panel open and the keyboard up — put it off
         * screen. The report was "nothing happened", when in fact the attempt
         * had failed and said so somewhere nobody could see.
         */}
        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-md)] bg-[var(--color-danger-soft)] px-3 py-2 text-sm text-[var(--color-danger)]"
          >
            {error}
          </p>
        )}

        {showServer ? (
          <ServerAddress onSaved={() => setError(null)} />
        ) : (
          /*
           * Always offered, never only inferred. Detection decides whether this
           * opens by default; it must not decide whether the address can be
           * reached at all, because getting that wrong strands someone on a
           * screen with no way forward — which is exactly what happened.
           */
          <button
            type="button"
            onClick={() => setShowServer(true)}
            className="flex w-full items-center justify-center gap-1.5 text-xs text-[var(--color-ink-faint)] hover:text-[var(--color-ink-soft)]"
          >
            <Server className="size-3.5" />
            Can't connect? Set the shop server address
          </button>
        )}

        <Field label="Email or login ID">
          <Input
            autoFocus
            autoComplete="username"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            placeholder="you@example.com"
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          loading={busy}
          disabled={!identifier || !password}
          icon={<LogIn className="size-4" />}
        >
          Sign in
        </Button>
      </form>
    </Shell>
  );
}

/**
 * Shown to an account still holding the password an admin set for it. The
 * server refuses everything else until this is done, so there is no way past.
 */
export function ChangePasswordScreen() {
  const { user, changePassword, signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Mirrors the server's rule so the problem shows before the round trip.
  const strength = next ? checkPassword(next, [user?.loginId ?? '', user?.email ?? '', user?.displayName ?? '']) : null;
  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = Boolean(current && next && confirm) && strength?.ok === true && !mismatch;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await changePassword(current, next);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the new password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Shell
      title="Set your password"
      subtitle={`Welcome, ${user?.displayName ?? ''}. Choose a password only you know.`}
    >
      <form onSubmit={submit} className="space-y-4">
        <p className="flex gap-2 rounded-[var(--radius-md)] bg-[var(--color-warn-soft)] px-3 py-2.5 text-xs text-[var(--color-warn)]">
          <ShieldCheck className="mt-px size-4 shrink-0" />
          <span>
            Your account still uses the starting password. Until you change it, nothing else in the app will open.
          </span>
        </p>

        <Field label="Current password">
          <Input
            autoFocus
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>

        <Field
          label="New password"
          hint="At least 8 characters, with a letter and a number."
          error={next && strength && !strength.ok ? strength.message : null}
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>

        <Field label="Repeat the new password" error={mismatch ? 'The two do not match.' : null}>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </Field>

        {error && (
          <p role="alert" className="rounded-[var(--radius-md)] bg-[var(--color-danger-soft)] px-3 py-2 text-sm text-[var(--color-danger)]">
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="primary"
          size="lg"
          full
          loading={busy}
          disabled={!ready}
          icon={<KeyRound className="size-4" />}
        >
          Save and continue
        </Button>

        <button
          type="button"
          onClick={() => void signOut()}
          className="w-full text-center text-xs text-[var(--color-ink-faint)] hover:text-[var(--color-ink)]"
        >
          Sign in as someone else
        </button>
      </form>
    </Shell>
  );
}

export function AuthLoading() {
  return (
    <div className={cx('flex min-h-full items-center justify-center bg-[var(--color-canvas)]')}>
      <Loader2 className="size-6 animate-spin text-[var(--color-ink-faint)]" aria-label="Loading" />
    </div>
  );
}
